import { useReactiveVar } from '@apollo/client';

import { featureFlagsVar } from '@/graphql/reactiveVars';
import { isFeatureFlagEnabled } from '@/modules/featureFlags/isFeatureFlagEnabled';
import { useAICapabilitiesEnabled } from '@/modules/tenant/TenantDetailsContext/hooks/useAICapabilitiesEnabled';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import {
  AuthorizationLevel,
  EmployeeTier,
  TenantProductTier,
  UserKind,
} from '@/types/schema';
import { UnreachableError } from '@/utils/errors';

import { Authentication_ApplicationAuthorizationFragment } from '../graphql/Authentication.generated';
import { useCurrentUser } from './useCurrentUser';

export enum AuthorizationStatus {
  NOT_ALLOWED = 'NOT_ALLOWED',
  TIER_NOT_ENABLED = 'TIER_NOT_ENABLED',
  ALLOWED = 'ALLOWED',
}

const AUTHORIZATION_LEVEL_ORDER = [
  AuthorizationLevel.None,
  AuthorizationLevel.View,
  AuthorizationLevel.Editor,
  AuthorizationLevel.Admin,
];

// only exporting this for tests; it shouldn't be used
// outside of this file
export function hasSufficientAuthLevel(
  requiredLevel: AuthorizationLevel,
  actualLevel: AuthorizationLevel
) {
  const requiredLevelIndex =
    AUTHORIZATION_LEVEL_ORDER.indexOf(requiredLevel) ?? 0;
  const actualLevelIndex = AUTHORIZATION_LEVEL_ORDER.indexOf(actualLevel) ?? 0;
  return actualLevelIndex >= requiredLevelIndex;
}

export enum BehaviorAuthorizationType {
  // INTERNAL
  CAN_ACCESS_INTERNAL_TOOLS = 'CAN_ACCESS_INTERNAL_TOOLS',

  // ACTIONS
  CAN_REASSIGN_RELATIONSHIP_OWNER = 'CAN_REASSIGN_RELATIONSHIP_OWNER',
  CAN_UPDATE_SUB_BRAND = 'CAN_UPDATE_SUB_BRAND',
  CAN_MODIFY_HOUSEHOLD_ACCESS = 'CAN_MODIFY_HOUSEHOLD_ACCESS',
  CAN_EDIT_COLLABORATORS = 'CAN_EDIT_COLLABORATORS',
  CAN_VIEW_COLLABORATORS = 'CAN_VIEW_COLLABORATORS',
  CAN_CREATE_CLIENTS = 'CAN_CREATE_CLIENTS',
  CAN_EDIT_CLIENT_DATA = 'CAN_EDIT_CLIENT_DATA',
  CAN_MANAGE_INTEGRATION_DATA = 'CAN_MANAGE_INTEGRATION_DATA',
  CAN_MANAGE_SFTP = 'CAN_MANAGE_SFTP',
  // This is currently the same as CAN_EDIT_CLIENT_DATA, but we expect to split them out in the future
  CAN_CREATE_EDIT_PRESENTATIONS = 'CAN_CREATE_EDIT_PRESENTATIONS',
  // This is currently the same as CAN_EDIT_CLIENT_DATA, but we expect to split them out in the future
  CAN_CREATE_DOCUMENTS = 'CAN_CREATE_DOCUMENTS',
  CAN_VIEW_TASKS = 'CAN_VIEW_TASKS',
  CAN_DELETE_ENTITIES_AND_PROPOSALS = 'CAN_DELETE_ENTITIES_AND_PROPOSALS',
  CAN_DELETE_CLIENTS = 'CAN_DELETE_CLIENTS',
  CAN_MODIFY_ASSET_CATEGORIES = 'CAN_MODIFY_ASSET_CATEGORIES',
  CAN_EDIT_DISPOSITIVE_PROVISIONS = 'CAN_EDIT_DISPOSITIVE_PROVISIONS',
  CAN_PRESENT_ESTATE_WATERFALLS = 'CAN_PRESENT_ESTATE_WATERFALLS',
  CAN_CREATE_UPDATE_DELETE_ESTATE_WATERFALLS = 'CAN_CREATE_UPDATE_DELETE_ESTATE_WATERFALLS',
  CAN_MODIFY_HYPOTHETICAL_TRANSFERS = 'CAN_MODIFY_HYPOTHETICAL_TRANSFERS',
  CAN_MANAGE_ORGANIZATION_DISCLAIMERS = 'CAN_MANAGE_ORGANIZATION_DISCLAIMERS',
  CAN_PUBLISH_TENANT_PRESENTATION_TEMPLATES = 'CAN_PUBLISH_TENANT_PRESENTATION_TEMPLATES',

  // PAGES
  CAN_ACCESS_HOME_PAGE = 'CAN_ACCESS_HOME_PAGE',
  CAN_ACCESS_TASKS_PAGE = 'CAN_ACCESS_TASKS_PAGE',
  CAN_ACCESS_COLLABORATORS_PAGE = 'CAN_ACCESS_COLLABORATORS_PAGE',
  CAN_ACCESS_USER_ADMIN_PAGE = 'CAN_ACCESS_USER_ADMIN_PAGE',
  CAN_ACCESS_TEAMS_ADMIN_PAGE = 'CAN_ACCESS_TEAMS_ADMIN_PAGE',
  CAN_ACCESS_INTEGRATIONS_ADMIN_PAGE = 'CAN_ACCESS_INTEGRATIONS_ADMIN_PAGE',
  CAN_ACCESS_BRANDING_ADMIN_PAGE = 'CAN_ACCESS_BRANDING_ADMIN_PAGE',
  CAN_ACCESS_SUB_BRANDS_ADMIN_PAGE = 'CAN_ACCESS_SUB_BRANDS_ADMIN_PAGE',
  CAN_ACCESS_TENANT_PRESENTATION_TEMPLATES_ADMIN_PAGE = 'CAN_ACCESS_TENANT_PRESENTATION_TEMPLATES_ADMIN_PAGE',
  CAN_APPROVE_PRESENTATIONS = 'CAN_APPROVE_PRESENTATIONS',
  CAN_ACCESS_USER_SETTINGS_PAGE = 'CAN_ACCESS_USER_SETTINGS_PAGE',
  CAN_ACCESS_LEGAL_DISCLAIMERS_PAGE = 'CAN_ACCESS_LEGAL_DISCLAIMERS_PAGE',
  CAN_ACCESS_ADMIN_CATEGORIES_PAGES = 'CAN_ACCESS_ADMIN_CATEGORIES_PAGES',
  CAN_ACCESS_AUDIT_LOG_PAGE = 'CAN_ACCESS_AUDIT_LOG_PAGE',
  CAN_ACCESS_ROLES_ADMIN_PAGE = 'CAN_ACCESS_ROLES_ADMIN_PAGE',
  CAN_ACCESS_SECURITY_ADMIN_PAGE = 'CAN_ACCESS_SECURITY_ADMIN_PAGE',
  CAN_EDIT_SECURITY_ADMIN_PAGE = 'CAN_EDIT_SECURITY_ADMIN_PAGE',
  CAN_ACCESS_HOUSEHOLD_ACCESS_PAGE = 'CAN_ACCESS_HOUSEHOLD_ACCESS_PAGE',
  CAN_ACCESS_SUMMARY_PROMPTS_ADMIN_PAGE = 'CAN_ACCESS_SUMMARY_PROMPTS_ADMIN_PAGE',
  CAN_ACCESS_EDUCATION_ADMIN_PAGE = 'CAN_ACCESS_EDUCATION_ADMIN_PAGE',
  CAN_ACCESS_REPORTING_ADMIN_PAGE = 'CAN_ACCESS_REPORTING_ADMIN_PAGE',

  CAN_VIEW_HOUSEHOLDS = 'CAN_VIEW_HOUSEHOLDS',
}

/**
 * @description THIS FUNCTION SHOULD NOT BE USED OUTSIDE OF THIS FILE. It's exported for testing. Use useHasBehaviorAuthorization instead.
 *
 * Given a behavior and an authorization object, this function determines whether or not the the authorization
 * object has the right to perform the behavior.
 */
export function private_hasBehaviorAuthorization(
  type: BehaviorAuthorizationType,
  authorization: Authentication_ApplicationAuthorizationFragment,
  tier: EmployeeTier,
  tenantTier: TenantProductTier,
  isAICapabilitiesEnabled: boolean
): AuthorizationStatus {
  const canEditHouseholdData = hasSufficientAuthLevel(
    AuthorizationLevel.Editor,
    authorization.households
  );

  switch (type) {
    // INTERNAL
    case BehaviorAuthorizationType.CAN_ACCESS_INTERNAL_TOOLS:
      return authorization.userKind === UserKind.LuminaryInternal
        ? AuthorizationStatus.ALLOWED
        : AuthorizationStatus.NOT_ALLOWED;

    // ACTIONS
    case BehaviorAuthorizationType.CAN_CREATE_EDIT_PRESENTATIONS:
    // falls through to CAN_EDIT_CLIENT_DATA
    case BehaviorAuthorizationType.CAN_CREATE_DOCUMENTS:
    // falls through to CAN_EDIT_CLIENT_DATA
    case BehaviorAuthorizationType.CAN_EDIT_CLIENT_DATA:
      return canEditHouseholdData
        ? AuthorizationStatus.ALLOWED
        : AuthorizationStatus.NOT_ALLOWED;

    case BehaviorAuthorizationType.CAN_MANAGE_SFTP:
      return authorization.canManageSFTP
        ? AuthorizationStatus.ALLOWED
        : AuthorizationStatus.NOT_ALLOWED;

    case BehaviorAuthorizationType.CAN_MANAGE_INTEGRATION_DATA:
      if (
        canEditHouseholdData ||
        authorization.userKind === UserKind.LuminaryInternal
      ) {
        return AuthorizationStatus.ALLOWED;
      }

      return AuthorizationStatus.NOT_ALLOWED;

    case BehaviorAuthorizationType.CAN_EDIT_DISPOSITIVE_PROVISIONS:
      return canEditHouseholdData && authorization.canEditDispositiveProvisions
        ? AuthorizationStatus.ALLOWED
        : AuthorizationStatus.NOT_ALLOWED;

    case BehaviorAuthorizationType.CAN_PRESENT_ESTATE_WATERFALLS:
      return canEditHouseholdData && authorization.canPresentEstateWaterfall
        ? AuthorizationStatus.ALLOWED
        : AuthorizationStatus.NOT_ALLOWED;

    case BehaviorAuthorizationType.CAN_CREATE_UPDATE_DELETE_ESTATE_WATERFALLS:
    // falls through to CAN_MODIFY_HYPOTHETICAL_TRANSFERS
    case BehaviorAuthorizationType.CAN_MODIFY_HYPOTHETICAL_TRANSFERS:
      if (!canEditHouseholdData || !authorization.canEditEstateWaterfall) {
        return AuthorizationStatus.NOT_ALLOWED;
      }

      return tier === EmployeeTier.Advanced
        ? AuthorizationStatus.ALLOWED
        : AuthorizationStatus.TIER_NOT_ENABLED;
    case BehaviorAuthorizationType.CAN_REASSIGN_RELATIONSHIP_OWNER:
      return hasSufficientAuthLevel(
        AuthorizationLevel.Admin,
        authorization.households
      )
        ? AuthorizationStatus.ALLOWED
        : AuthorizationStatus.NOT_ALLOWED;
    case BehaviorAuthorizationType.CAN_UPDATE_SUB_BRAND:
      return hasSufficientAuthLevel(
        AuthorizationLevel.Editor,
        authorization.households
      )
        ? AuthorizationStatus.ALLOWED
        : AuthorizationStatus.NOT_ALLOWED;
    case BehaviorAuthorizationType.CAN_MODIFY_HOUSEHOLD_ACCESS:
      return canEditHouseholdData
        ? AuthorizationStatus.ALLOWED
        : AuthorizationStatus.NOT_ALLOWED;

    case BehaviorAuthorizationType.CAN_DELETE_ENTITIES_AND_PROPOSALS:
      return canEditHouseholdData
        ? AuthorizationStatus.ALLOWED
        : AuthorizationStatus.NOT_ALLOWED;

    case BehaviorAuthorizationType.CAN_DELETE_CLIENTS:
      if (authorization.userKind === UserKind.LuminaryInternal) {
        return AuthorizationStatus.ALLOWED;
      }

      return hasSufficientAuthLevel(
        AuthorizationLevel.Admin,
        authorization.households
      )
        ? AuthorizationStatus.ALLOWED
        : AuthorizationStatus.NOT_ALLOWED;
    case BehaviorAuthorizationType.CAN_CREATE_CLIENTS:
      // Luminary users aren't eligible as relationshop owners in prod, but our permissions allow us to
      // create clients and we want to expose the ability to do so.
      if (authorization.userKind === UserKind.LuminaryInternal) {
        return AuthorizationStatus.ALLOWED;
      }

      if (authorization.userKind === UserKind.ThirdParty) {
        return AuthorizationStatus.NOT_ALLOWED;
      }

      // Check auth level first - if they don't have sufficient auth level, they can't create clients
      if (
        !hasSufficientAuthLevel(
          AuthorizationLevel.Editor,
          authorization.households
        )
      ) {
        return AuthorizationStatus.NOT_ALLOWED;
      }

      // If they have sufficient auth level but can't act as relationship owner, they're not allowed
      if (!authorization.canActAsRelationshipOwner) {
        return AuthorizationStatus.NOT_ALLOWED;
      }

      // If they have sufficient auth level and can act as relationship owner, they're allowed
      return AuthorizationStatus.ALLOWED;

    case BehaviorAuthorizationType.CAN_VIEW_TASKS:
      return authorization.userKind !== UserKind.ThirdParty
        ? AuthorizationStatus.ALLOWED
        : AuthorizationStatus.NOT_ALLOWED;

    case BehaviorAuthorizationType.CAN_MODIFY_ASSET_CATEGORIES:
      return authorization.canModifyGlobalCategories
        ? AuthorizationStatus.ALLOWED
        : AuthorizationStatus.NOT_ALLOWED;

    case BehaviorAuthorizationType.CAN_MANAGE_ORGANIZATION_DISCLAIMERS:
      return authorization.canManageOrganizationDisclaimers
        ? AuthorizationStatus.ALLOWED
        : AuthorizationStatus.NOT_ALLOWED;

    case BehaviorAuthorizationType.CAN_PUBLISH_TENANT_PRESENTATION_TEMPLATES:
      return hasSufficientAuthLevel(
        AuthorizationLevel.Admin,
        authorization.tenantPresentationTemplates
      )
        ? AuthorizationStatus.ALLOWED
        : AuthorizationStatus.NOT_ALLOWED;

    case BehaviorAuthorizationType.CAN_EDIT_COLLABORATORS:
      return hasSufficientAuthLevel(
        AuthorizationLevel.Editor,
        authorization.collaborators
      )
        ? AuthorizationStatus.ALLOWED
        : AuthorizationStatus.NOT_ALLOWED;

    case BehaviorAuthorizationType.CAN_VIEW_COLLABORATORS:
      return hasSufficientAuthLevel(
        AuthorizationLevel.View,
        authorization.collaborators
      )
        ? AuthorizationStatus.ALLOWED
        : AuthorizationStatus.NOT_ALLOWED;

    // PAGES
    case BehaviorAuthorizationType.CAN_ACCESS_HOME_PAGE:
    // falls through to shared handling with tasks page
    case BehaviorAuthorizationType.CAN_ACCESS_TASKS_PAGE:
      return private_hasBehaviorAuthorization(
        BehaviorAuthorizationType.CAN_VIEW_TASKS,
        authorization,
        tier,
        tenantTier,
        isAICapabilitiesEnabled
      );

    case BehaviorAuthorizationType.CAN_ACCESS_COLLABORATORS_PAGE:
      if (tier === EmployeeTier.Basic) {
        return AuthorizationStatus.TIER_NOT_ENABLED;
      }

      return hasSufficientAuthLevel(
        AuthorizationLevel.Editor,
        authorization.collaborators
      )
        ? AuthorizationStatus.ALLOWED
        : AuthorizationStatus.NOT_ALLOWED;

    case BehaviorAuthorizationType.CAN_ACCESS_SUB_BRANDS_ADMIN_PAGE:
    // falls through to shared handling with branding admin page
    case BehaviorAuthorizationType.CAN_ACCESS_BRANDING_ADMIN_PAGE:
      return hasSufficientAuthLevel(
        AuthorizationLevel.Editor,
        authorization.branding
      )
        ? AuthorizationStatus.ALLOWED
        : AuthorizationStatus.NOT_ALLOWED;

    case BehaviorAuthorizationType.CAN_ACCESS_INTEGRATIONS_ADMIN_PAGE:
      // TODO: maybe this should be its own permission rather than be the
      // internalEmployees one?
      return hasSufficientAuthLevel(
        AuthorizationLevel.Admin,
        authorization.internalEmployees
      )
        ? AuthorizationStatus.ALLOWED
        : AuthorizationStatus.NOT_ALLOWED;

    case BehaviorAuthorizationType.CAN_ACCESS_TEAMS_ADMIN_PAGE:
    // falls through to shared handling with user admin
    case BehaviorAuthorizationType.CAN_ACCESS_USER_ADMIN_PAGE:
      return hasSufficientAuthLevel(
        AuthorizationLevel.Admin,
        authorization.internalEmployees
      )
        ? AuthorizationStatus.ALLOWED
        : AuthorizationStatus.NOT_ALLOWED;

    case BehaviorAuthorizationType.CAN_ACCESS_USER_SETTINGS_PAGE: {
      // as of this decision, the only functionality in the user setting page is the addepar
      // auth flow for single users, which isn't relevant to third parties.
      return authorization.userKind !== UserKind.ThirdParty
        ? AuthorizationStatus.ALLOWED
        : AuthorizationStatus.NOT_ALLOWED;
    }

    case BehaviorAuthorizationType.CAN_ACCESS_LEGAL_DISCLAIMERS_PAGE: {
      return hasSufficientAuthLevel(
        AuthorizationLevel.Editor,
        authorization.branding
      )
        ? AuthorizationStatus.ALLOWED
        : AuthorizationStatus.NOT_ALLOWED;
    }

    case BehaviorAuthorizationType.CAN_ACCESS_ADMIN_CATEGORIES_PAGES: {
      return authorization.canModifyGlobalCategories
        ? AuthorizationStatus.ALLOWED
        : AuthorizationStatus.NOT_ALLOWED;
    }

    case BehaviorAuthorizationType.CAN_ACCESS_AUDIT_LOG_PAGE: {
      return hasSufficientAuthLevel(
        AuthorizationLevel.View,
        authorization.auditLogs
      )
        ? AuthorizationStatus.ALLOWED
        : AuthorizationStatus.NOT_ALLOWED;
    }

    case BehaviorAuthorizationType.CAN_ACCESS_ROLES_ADMIN_PAGE: {
      if (authorization.userKind === UserKind.LuminaryInternal) {
        return hasSufficientAuthLevel(
          AuthorizationLevel.View,
          authorization.roles
        )
          ? AuthorizationStatus.ALLOWED
          : AuthorizationStatus.NOT_ALLOWED;
      }

      if (tenantTier !== TenantProductTier.Enterprise) {
        return AuthorizationStatus.NOT_ALLOWED;
      }

      return hasSufficientAuthLevel(
        AuthorizationLevel.View,
        authorization.roles
      )
        ? AuthorizationStatus.ALLOWED
        : AuthorizationStatus.NOT_ALLOWED;
    }

    case BehaviorAuthorizationType.CAN_ACCESS_SECURITY_ADMIN_PAGE: {
      // Luminary internal users can always view the security page (read-only)
      if (authorization.userKind === UserKind.LuminaryInternal) {
        return AuthorizationStatus.ALLOWED;
      }

      if (tenantTier !== TenantProductTier.Enterprise) {
        return AuthorizationStatus.NOT_ALLOWED;
      }

      return authorization.canApproveLuminarySupportTeamAccess
        ? AuthorizationStatus.ALLOWED
        : AuthorizationStatus.NOT_ALLOWED;
    }

    case BehaviorAuthorizationType.CAN_EDIT_SECURITY_ADMIN_PAGE: {
      // Luminary internal users can view but not edit
      if (authorization.userKind === UserKind.LuminaryInternal) {
        return AuthorizationStatus.NOT_ALLOWED;
      }

      if (tenantTier !== TenantProductTier.Enterprise) {
        return AuthorizationStatus.NOT_ALLOWED;
      }

      return authorization.canApproveLuminarySupportTeamAccess
        ? AuthorizationStatus.ALLOWED
        : AuthorizationStatus.NOT_ALLOWED;
    }

    case BehaviorAuthorizationType.CAN_ACCESS_HOUSEHOLD_ACCESS_PAGE: {
      const canEditHousehold = hasSufficientAuthLevel(
        AuthorizationLevel.Editor,
        authorization.households
      );

      const canViewEmployees = hasSufficientAuthLevel(
        AuthorizationLevel.View,
        authorization.internalEmployees
      );

      return canEditHousehold && canViewEmployees
        ? AuthorizationStatus.ALLOWED
        : AuthorizationStatus.NOT_ALLOWED;
    }

    case BehaviorAuthorizationType.CAN_VIEW_HOUSEHOLDS: {
      return hasSufficientAuthLevel(
        AuthorizationLevel.View,
        authorization.households
      )
        ? AuthorizationStatus.ALLOWED
        : AuthorizationStatus.NOT_ALLOWED;
    }

    case BehaviorAuthorizationType.CAN_ACCESS_SUMMARY_PROMPTS_ADMIN_PAGE: {
      if (!isAICapabilitiesEnabled) {
        return AuthorizationStatus.NOT_ALLOWED;
      }

      return authorization.canModifyPrompts
        ? AuthorizationStatus.ALLOWED
        : AuthorizationStatus.NOT_ALLOWED;
    }

    case BehaviorAuthorizationType.CAN_ACCESS_EDUCATION_ADMIN_PAGE: {
      return hasSufficientAuthLevel(
        AuthorizationLevel.Editor,
        authorization.branding
      )
        ? AuthorizationStatus.ALLOWED
        : AuthorizationStatus.NOT_ALLOWED;
    }

    case BehaviorAuthorizationType.CAN_ACCESS_TENANT_PRESENTATION_TEMPLATES_ADMIN_PAGE: {
      return hasSufficientAuthLevel(
        AuthorizationLevel.Editor,
        authorization.tenantPresentationTemplates
      )
        ? AuthorizationStatus.ALLOWED
        : AuthorizationStatus.NOT_ALLOWED;
    }

    case BehaviorAuthorizationType.CAN_APPROVE_PRESENTATIONS: {
      return authorization.canApprovePresentations
        ? AuthorizationStatus.ALLOWED
        : AuthorizationStatus.NOT_ALLOWED;
    }

    case BehaviorAuthorizationType.CAN_ACCESS_REPORTING_ADMIN_PAGE: {
      return authorization.canAccessTenantReporting &&
        isFeatureFlagEnabled('tenant_reporting_admin_section')
        ? AuthorizationStatus.ALLOWED
        : AuthorizationStatus.NOT_ALLOWED;
    }

    default:
      throw new UnreachableError({
        case: type,
        message: `Unhandled BehaviorAuthorizationType ${type}`,
      });
  }
}

/**
 * Options shared by the behavior-authorization hooks. Reuse or extend this for
 * other hooks/callers that need the same missing-user behavior.
 *
 * Intended for print mode (and the read-only presentation viewer): there's no
 * authenticated user to check against, so rather than throw we can safely
 * assume the user isn't authorized and return `false`.
 */
export interface MissingUserAuthorizationOptions {
  /**
   * When true, return "not authorized" (false / NOT_ALLOWED) instead of
   * throwing in contexts that render without an authenticated user (e.g. the
   * read-only presentation viewer / print export).
   */
  returnFalseOnMissingUser?: boolean;
}

/**
 * @description Given a BehaviorAuthorizationType, this function determines whether or not the currently-authenticated
 * user can perform that behavior.
 * @param type the type of behavior to check the permission of
 * @param options configuration options for the hook
 * @returns AuthorizationStatus indicating whether the user has the right, and if not, why
 */
export function useHasBehaviorAuthorization(
  type: BehaviorAuthorizationType,
  options?: MissingUserAuthorizationOptions & { detailed: true }
): AuthorizationStatus;
export function useHasBehaviorAuthorization(
  type: BehaviorAuthorizationType,
  options?: MissingUserAuthorizationOptions & { detailed?: false }
): boolean;
export function useHasBehaviorAuthorization(
  type: BehaviorAuthorizationType,
  options?: MissingUserAuthorizationOptions & { detailed?: boolean }
): AuthorizationStatus | boolean {
  // Recheck flag-based authorizations when the server's flag evaluations
  // arrive or change.
  useReactiveVar(featureFlagsVar);
  const currentUser = useCurrentUser();
  const { tier: tenantTier } = useTenantDetailsContext();
  const isAICapabilitiesEnabled = useAICapabilitiesEnabled();
  // All hooks above run unconditionally, so the no-user check below never
  // changes the hook order. The two context hooks above can't be the missing
  // dependency this option guards against: TenantDetailsProvider is mounted
  // above UserProvider at the app root, so wherever currentUser is readable
  // those contexts are too — only the currentUser *value* is absent in some
  // contexts (e.g. the read-only presentation viewer / print export). Callers
  // there pass returnFalseOnMissingUser to treat "no user" as "not authorized"
  // rather than crashing.
  if (!currentUser) {
    if (options?.returnFalseOnMissingUser) {
      return options.detailed ? AuthorizationStatus.NOT_ALLOWED : false;
    }
    throw new Error('Cannot call useBehaviorAuthorization with no currentUser');
  }

  const status = private_hasBehaviorAuthorization(
    type,
    currentUser.authorization,
    currentUser.tier,
    tenantTier,
    isAICapabilitiesEnabled
  );

  // If detailed mode is requested, return the full status
  if (options?.detailed) {
    return status;
  }

  // Otherwise maintain backward compatibility by returning a boolean
  return status === AuthorizationStatus.ALLOWED;
}
